import type {
  LoadedScene,
  PlayCanvasLodOverrides,
  WorldFlythrough,
  WorldFlythroughChapter,
  WorldFlythroughCue,
  WorldFlythroughKeyframe,
  WorldLabel,
  WorldInteractable,
  WorldPanel,
  WorldZone,
} from '@viewer/world/worldTypes';

interface WorldScene {
  splat?: string;
  mobileSplat?: string;
  splatStreaming?: unknown;
  splatOption2?: string;
  collision?: string;
  cameraStart?: unknown;
  cameraYaw?: unknown;
  background?: unknown;
  playCanvasBasis?: unknown;
  lodSplatCount?: unknown;
  lodByteCounts?: unknown;
  mobileLodSplatCount?: unknown;
  revealRadius?: unknown;
  pointPreview?: unknown;
  mobilePointPreview?: unknown;
  position?: unknown;
  rotation?: unknown;
  scale?: unknown;
  panels?: unknown;
  interactables?: unknown;
  labels?: unknown;
  zones?: unknown;
  flythroughs?: unknown;
  lod?: unknown;
  lodMobile?: unknown;
}

interface WorldConfig {
  scenes?: WorldScene[];
}

function parseVec3(value: unknown, fallback: [number, number, number]): [number, number, number] {
  if (Array.isArray(value) && value.length === 3) {
    const [x, y, z] = value;
    if (typeof x === 'number' && typeof y === 'number' && typeof z === 'number') {
      return [x, y, z];
    }
  }
  return fallback;
}

function parseBackground(value: unknown): string | undefined {
  if (typeof value === 'string' && value.length > 0) return value;
  return undefined;
}

function parseScale(value: unknown): [number, number, number] {
  if (typeof value === 'number' && Number.isFinite(value)) return [value, value, value];
  return parseVec3(value, [1, 1, 1]);
}

function parseOptionalVec3(value: unknown): [number, number, number] | undefined {
  if (Array.isArray(value) && value.length === 3) {
    const [x, y, z] = value;
    if (typeof x === 'number' && typeof y === 'number' && typeof z === 'number') {
      return [x, y, z];
    }
  }
  return undefined;
}

function parseFlythroughKeyframe(value: unknown): WorldFlythroughKeyframe | null {
  if (typeof value !== 'object' || value === null) return null;
  const raw = value as Record<string, unknown>;
  const position = parseOptionalVec3(raw.position);
  if (!position) return null;
  const out: WorldFlythroughKeyframe = { position };
  if (typeof raw.time === 'number' && Number.isFinite(raw.time)) out.time = raw.time;
  if (typeof raw.timeMs === 'number' && Number.isFinite(raw.timeMs)) out.timeMs = raw.timeMs;
  const target = parseOptionalVec3(raw.target);
  if (target) out.target = target;
  const lookAt = parseOptionalVec3(raw.lookAt);
  if (lookAt) out.lookAt = lookAt;
  const rotation = parseOptionalVec3(raw.rotation);
  if (rotation) out.rotation = rotation;
  if (typeof raw.fov === 'number' && Number.isFinite(raw.fov)) out.fov = raw.fov;
  return out;
}

function parseFlythroughCue(value: unknown): WorldFlythroughCue | null {
  if (typeof value !== 'object' || value === null) return null;
  const raw = value as Record<string, unknown>;
  if (typeof raw.action !== 'string' || raw.action.length === 0) return null;
  const out: WorldFlythroughCue = { action: raw.action };
  if (typeof raw.at === 'number' && Number.isFinite(raw.at)) out.at = raw.at;
  if (typeof raw.time === 'number' && Number.isFinite(raw.time)) out.time = raw.time;
  if (typeof raw.timeMs === 'number' && Number.isFinite(raw.timeMs)) out.timeMs = raw.timeMs;
  return out;
}

function parseFlythroughChapter(value: unknown): WorldFlythroughChapter | null {
  if (typeof value !== 'object' || value === null) return null;
  const raw = value as Record<string, unknown>;
  if (typeof raw.label !== 'string' || raw.label.length === 0) return null;
  const id =
    typeof raw.id === 'string' && raw.id.length > 0
      ? raw.id
      : raw.label
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
  if (id.length === 0) return null;
  const out: WorldFlythroughChapter = { id, label: raw.label };
  if (typeof raw.at === 'number' && Number.isFinite(raw.at)) out.at = raw.at;
  if (typeof raw.time === 'number' && Number.isFinite(raw.time)) out.time = raw.time;
  if (typeof raw.timeMs === 'number' && Number.isFinite(raw.timeMs)) out.timeMs = raw.timeMs;
  return out;
}

function parseFlythroughs(value: unknown): WorldFlythrough[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const flythroughs: WorldFlythrough[] = [];
  for (const item of value) {
    if (typeof item !== 'object' || item === null) continue;
    const raw = item as Record<string, unknown>;
    if (typeof raw.id !== 'string' || raw.id.length === 0) continue;
    const keyframes = Array.isArray(raw.keyframes)
      ? raw.keyframes.map(parseFlythroughKeyframe).filter((k): k is WorldFlythroughKeyframe => Boolean(k))
      : [];
    if (keyframes.length < 2) continue;
    const cues = Array.isArray(raw.cues)
      ? raw.cues.map(parseFlythroughCue).filter((cue): cue is WorldFlythroughCue => Boolean(cue))
      : undefined;
    const chapters = Array.isArray(raw.chapters)
      ? raw.chapters
          .map(parseFlythroughChapter)
          .filter((chapter): chapter is WorldFlythroughChapter => Boolean(chapter))
      : undefined;
    const flythrough: WorldFlythrough = {
      id: raw.id,
      label: typeof raw.label === 'string' && raw.label.length > 0 ? raw.label : raw.id,
      keyframes,
    };
    if (typeof raw.durationMs === 'number' && Number.isFinite(raw.durationMs) && raw.durationMs > 0) {
      flythrough.durationMs = raw.durationMs;
    }
    if (raw.interpolation === 'spline' || raw.interpolation === 'linear' || raw.interpolation === 'step') {
      flythrough.interpolation = raw.interpolation;
    }
    if (chapters && chapters.length > 0) flythrough.chapters = chapters;
    if (cues && cues.length > 0) flythrough.cues = cues;
    if (typeof raw.onStart === 'string' && raw.onStart.length > 0) flythrough.onStart = raw.onStart;
    if (typeof raw.onStop === 'string' && raw.onStop.length > 0) flythrough.onStop = raw.onStop;
    if (typeof raw.onComplete === 'string' && raw.onComplete.length > 0) {
      flythrough.onComplete = raw.onComplete;
    }
    flythroughs.push(flythrough);
  }
  return flythroughs.length > 0 ? flythroughs : undefined;
}

function parseLodOverrides(value: unknown): PlayCanvasLodOverrides | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const raw = value as Record<string, unknown>;
  const out: PlayCanvasLodOverrides = {};
  const num = (key: keyof PlayCanvasLodOverrides) => {
    const v = raw[key as string];
    if (typeof v === 'number' && Number.isFinite(v)) {
      (out as Record<string, number>)[key as string] = v;
    }
  };
  num('splatBudgetMillions');
  num('lodRangeMin');
  num('lodRangeMax');
  num('lodBaseDistance');
  num('lodMultiplier');
  num('lodUpdateAngle');
  num('lodBehindPenalty');
  num('lodUpdateDistance');
  num('lodUnderfillLimit');
  num('minPixelSize');
  num('minContribution');
  num('orientationEulerX');
  if (typeof raw.radialSorting === 'boolean') {
    out.radialSorting = raw.radialSorting;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

function parseLodByteCounts(value: unknown): Record<number, number> | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const raw = value as Record<string, unknown>;
  const out: Record<number, number> = {};
  for (const [key, bytes] of Object.entries(raw)) {
    const lod = Number(key);
    if (!Number.isInteger(lod) || lod < 0) continue;
    if (typeof bytes === 'number' && Number.isFinite(bytes) && bytes > 0) {
      out[lod] = Math.round(bytes);
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

export async function loadWorldScene(worldId: string): Promise<LoadedScene> {
  const response = await fetch(`/worlds/${worldId}/world.json`, { cache: 'no-store' });
  if (!response.ok) throw new Error(`World config request failed (${response.status})`);

  const world = (await response.json()) as WorldConfig;
  const scene = world.scenes?.[0];
  const maybeSplat = scene?.splat;
  if (typeof maybeSplat !== 'string' || maybeSplat.length === 0) {
    throw new Error('world.json scenes[0].splat is missing');
  }

  const panels: WorldPanel[] = Array.isArray(scene?.panels)
    ? (scene.panels as unknown[]).filter((p): p is WorldPanel => {
        if (typeof p !== 'object' || p === null) return false;
        const panel = p as Record<string, unknown>;
        return Array.isArray(panel.position) && (panel.position as unknown[]).length === 3;
      })
    : [];

  const interactables: WorldInteractable[] = Array.isArray(scene?.interactables)
    ? (scene.interactables as unknown[]).filter((item): item is WorldInteractable => {
        if (typeof item !== 'object' || item === null) return false;
        const obj = item as Record<string, unknown>;
        return (
          typeof obj.id === 'string' &&
          Array.isArray(obj.position) &&
          (obj.position as unknown[]).length === 3 &&
          typeof obj.onClick === 'string'
        );
      })
    : [];

  const labels: WorldLabel[] = Array.isArray(scene?.labels)
    ? (scene.labels as unknown[]).filter((item): item is WorldLabel => {
        if (typeof item !== 'object' || item === null) return false;
        const obj = item as Record<string, unknown>;
        return (
          typeof obj.id === 'string' &&
          typeof obj.text === 'string' &&
          obj.text.length > 0 &&
          Array.isArray(obj.position) &&
          (obj.position as unknown[]).length === 3
        );
      })
    : [];

  const zones: WorldZone[] = Array.isArray(scene?.zones)
    ? (scene.zones as unknown[]).filter((item): item is WorldZone => {
        if (typeof item !== 'object' || item === null) return false;
        const obj = item as Record<string, unknown>;
        return (
          typeof obj.id === 'string' &&
          Array.isArray(obj.center) &&
          (obj.center as unknown[]).length === 3 &&
          typeof obj.radius === 'number'
        );
      })
    : [];

  const maybeLod = scene?.lodSplatCount;
  const maybeMobileLod = scene?.mobileLodSplatCount;
  const maybeReveal = scene?.revealRadius;

  return {
    splatUrl: maybeSplat,
    mobileSplatUrl:
      typeof scene?.mobileSplat === 'string' && scene.mobileSplat.length > 0
        ? scene.mobileSplat
        : undefined,
    splatStreaming:
      typeof scene?.splatStreaming === 'boolean' ? scene.splatStreaming : undefined,
    splatOption2:
      typeof scene?.splatOption2 === 'string' && scene.splatOption2.length > 0
        ? scene.splatOption2
        : undefined,
    collisionUrl:
      typeof scene?.collision === 'string' && scene.collision.length > 0 ? scene.collision : undefined,
    cameraStart: parseVec3(scene?.cameraStart, [0, 1.6, 3.2]),
    cameraYaw: typeof scene?.cameraYaw === 'number' && Number.isFinite(scene.cameraYaw) ? scene.cameraYaw : 0,
    background: parseBackground(scene?.background),
    playCanvasBasis: typeof scene?.playCanvasBasis === 'boolean' ? scene.playCanvasBasis : undefined,
    lodSplatCount:
      typeof maybeLod === 'number' && Number.isFinite(maybeLod) && maybeLod > 0 ? Math.round(maybeLod) : undefined,
    lodByteCounts: parseLodByteCounts(scene?.lodByteCounts),
    mobileLodSplatCount:
      typeof maybeMobileLod === 'number' && Number.isFinite(maybeMobileLod) && maybeMobileLod > 0
        ? Math.round(maybeMobileLod)
        : undefined,
    lod: parseLodOverrides(scene?.lod),
    lodMobile: parseLodOverrides(scene?.lodMobile),
    revealRadius:
      typeof maybeReveal === 'number' && Number.isFinite(maybeReveal) && maybeReveal > 0
        ? maybeReveal
        : undefined,
    pointPreview:
      typeof scene?.pointPreview === 'string' && scene.pointPreview.length > 0
        ? scene.pointPreview
        : undefined,
    mobilePointPreview:
      typeof scene?.mobilePointPreview === 'string' && scene.mobilePointPreview.length > 0
        ? scene.mobilePointPreview
        : undefined,
    position: parseVec3(scene?.position, [0, 0, 0]),
    rotation: parseVec3(scene?.rotation, [0, 0, 0]),
    scale: parseScale(scene?.scale),
    panels,
    interactables,
    labels,
    zones,
    flythroughs: parseFlythroughs(scene?.flythroughs),
  };
}
